import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import walletService from "../../../services/WalletService";
import { emitCheckoutEvent } from "../../../services/checkoutEvents";
import { storage } from "../../../storageconstants";
import { debugLog } from "../../../utils";
import {
  setCheckoutBreakdownTotal,
  setCheckoutFeeBreakdownEnabled,
} from "../../../utils/checkoutFeeBreakdown";
import { translateError } from "../../../errorTranslations";

export default class extends Controller {
  static targets = ["confirmButton", "coins"];

  static values = {
    walletaddress: String,
    session: String,
  };

  connect() {
    this.codeValue = sessionStorage.getItem(storage.CODE);
    this.selectedTokenId = null;
    this.selectedNetworkId = null;

    setTimeout(() => {
      this.checkForAutoSelection();
      this.listenWalletChange();
      this.hideLoadingScreen();
    }, 100);

    this.boundHandleSubmitEnd = this.handleSubmitEnd.bind(this);
    document.addEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
  }

  disconnect() {
    document.removeEventListener("turbo:submit-end", this.boundHandleSubmitEnd);
  }

  async handleSubmitEnd(event) {
    const form = event.detail.formSubmission.formElement;

    if (form.id !== "frame-post-form") return;

    const fetchResponse = event.detail.fetchResponse;
    if (!fetchResponse) return;

    const contentType = fetchResponse.response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      try {
        const res = await fetchResponse.response;
        const jsonData = await res.json();

        if (jsonData.response_type === "redirect") {
          this.hideLoadingScreen();
          Turbo.visit(jsonData.redirect_url);
        }
      } catch (error) {
        debugLog("Error parsing JSON response:", error);
      }
    }
  }

  goBack(event) {
    event.preventDefault();

    const backTarget = sessionStorage.getItem(storage.THIRD_PARTY_BACK_TARGET);
    if (backTarget === "connect-wallet-only") {
      Turbo.visit(`/${this.codeValue}/connect-wallet-only?session=${this.sessionValue}`, {
        frame: "step_content",
        action: "replace",
      });
      return;
    }
    if (backTarget === "payment-options") {
      Turbo.visit(`/${this.codeValue}/payment-options?session=${this.sessionValue}`, {
        frame: "step_content",
        action: "replace",
      });
      return;
    }

    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    Turbo.visit(`/${this.codeValue}/payment-options?session=${this.sessionValue}`);
  }

  selectAsset(event) {
    const el = event.currentTarget;
    const tokenid = el.getAttribute("data-third-party-payment-options-tokenid-param");
    const networkid = el.getAttribute("data-third-party-payment-options-networkid-param");

    if (!tokenid || !networkid) return;

    this.selectedTokenId = tokenid;
    this.selectedNetworkId = networkid;

    emitCheckoutEvent("checkout_cw_asset_selected", {
      asset: el.dataset.symbol || "",
      network: el.dataset.networkName || "",
      token_id: tokenid,
      network_id: networkid,
      balance_available: el.dataset.balanceAvailable || "",
    });

    // Update visual selection using semantic connect-wallet asset classes.
    const selectedClasses = [
      "bg-thm-selector-asset-selector-active-bg",
      "border-thm-selector-asset-selector-active-border-color",
      "text-thm-selector-asset-selector-active-primary-text",
    ];
    const unselectedClasses = [
      "bg-thm-selector-asset-selector-passive-bg",
      "border-thm-selector-asset-selector-passive-border-color",
      "text-thm-selector-asset-selector-passive-primary-text",
    ];

    document.querySelectorAll(".asset-option").forEach((row) => {
      row.classList.remove(...selectedClasses);
      row.classList.add(...unselectedClasses);
    });

    el.classList.remove(...unselectedClasses);
    el.classList.add(...selectedClasses);

    this.updateCheckoutOverviewFromAsset(el);
    this.enableConfirmButton();
  }

  selectPaymentOption() {
    if (!this.selectedTokenId || !this.selectedNetworkId) {
      debugLog("No asset selected");
      return;
    }

    this.showLoadingScreen();
    const tokenid = this.selectedTokenId;
    const networkid = this.selectedNetworkId;

    this.renderPaymentMethod(tokenid, networkid);

    const form = document.getElementById("frame-post-form");
    form.action = `/${this.codeValue}/review?session=${this.sessionValue}`;
    document.getElementById("form_session").value = this.sessionValue;
    document.getElementById("form_address").value = this.walletaddressValue;
    document.getElementById("form_networkid").value = networkid;
    document.getElementById("form_tokenid").value = tokenid;
    form.requestSubmit();
  }

  checkForAutoSelection() {
    const autoSelected = document.querySelector(".asset-option[data-auto-selected='true']");
    if (autoSelected) {
      this.selectedTokenId = autoSelected.getAttribute("data-third-party-payment-options-tokenid-param");
      this.selectedNetworkId = autoSelected.getAttribute("data-third-party-payment-options-networkid-param");
      this.updateCheckoutOverviewFromAsset(autoSelected);
      this.enableConfirmButton();

      // Statsig funnel between wallet_connected and tx_approved.
      emitCheckoutEvent("checkout_cw_asset_selected", {
        asset: autoSelected.dataset.symbol || "",
        network: autoSelected.dataset.networkName || "",
        token_id: this.selectedTokenId,
        network_id: this.selectedNetworkId,
        balance_available: autoSelected.dataset.balanceAvailable || "",
      });
    }
  }

  updateCheckoutOverviewFromAsset(assetEl) {
    if (!assetEl) return;

    const amountEl = assetEl.querySelector(".shrink-0.font-medium");
    const amountDisplay = (amountEl?.textContent || "").trim();
    if (!amountDisplay) return;

    const feeDisplay = (assetEl.getAttribute("data-checkout-fee-display") || "").trim();
    if (feeDisplay) {
      const checkoutFees = document.getElementById("checkout_fees");
      if (checkoutFees) {
        checkoutFees.textContent = feeDisplay;
      }
      // Keep the mobile "incl. $X fees" summary in step with the headline.
      const inclFee = feeDisplay.replace(/\s*USD$/i, "");
      document.querySelectorAll(".js-checkout-fees-incl").forEach((el) => {
        el.textContent = `incl. ${inclFee} fees`;
      });
    }

    // The order-summary breakdown's network-fee row must track the selected asset so it
    // never contradicts the headline fee (headline = network + the constant processing fee).
    const networkFeeDisplay = (assetEl.getAttribute("data-checkout-network-fee-display") || "").trim();
    if (networkFeeDisplay) {
      document.querySelectorAll(".js-checkout-network-fee").forEach((el) => {
        el.textContent = networkFeeDisplay;
      });
      setCheckoutFeeBreakdownEnabled(true);
    }

    const checkoutTotalAmount = document.getElementById("checkout_total_amount");
    if (checkoutTotalAmount) {
      checkoutTotalAmount.textContent = amountDisplay;
    }

    const checkoutPriceMobile = document.getElementById("checkout_price_mobile");
    if (checkoutPriceMobile) {
      checkoutPriceMobile.textContent = `Est. ${amountDisplay}`;
    }

    setCheckoutBreakdownTotal(amountDisplay);
  }

  enableConfirmButton() {
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.classList.remove("is-disabled");
    }
  }

  async renderPaymentMethod(tokenId, networkId) {
    const code = sessionStorage.getItem(storage.CODE);
    const sessionId = sessionStorage.getItem(storage.SESSION);

    const httpReq = await fetch(`/${code}/payment-method`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Session": sessionId,
        "X-Code": code,
      },
      body: JSON.stringify({ tokenId, networkId }),
    });

    const jsonData = await httpReq.json();
    if (jsonData.error) return;

    if (jsonData.success && jsonData.type === "html") {
      Turbo.renderStreamMessage(jsonData.data);
    }
  }

  async connectWallet() {
    try {
      this.dispatch("listenWalletChange", { bubbles: true });
      this.showLoadingScreen();
      await walletService.connectWallet();
      this.hideLoadingScreen();
    } catch (error) {
      debugLog("Error connecting wallet:", error);
      this.hideLoadingScreen();
      this.dispatch("error", {
        detail: { content: translateError(error, "Failed to connect wallet. Please try again.") },
      });
    }
  }

  listenWalletChange() {
    this.dispatch("listenWalletChange");
  }

  showLoadingScreen() {
    this.dispatch("showLoadingScreen", { detail: { text: "Please Wait" } });
  }

  hideLoadingScreen() {
    this.dispatch("hideLoadingScreen");
  }
}
